// AuthProvider — resolves the current session on boot and exposes it
// through useAuth(). The frontend has no password surface: the only
// signal that the user is signed in is a successful GET /me behind the
// HttpOnly `citycare_session` cookie (ADR-012). This provider owns
// that single bootstrap request and the resulting state machine.
//
// States:
//   * 'loading'         — /me request is in flight
//   * 'authenticated'   — /me returned an AuthData payload (or a
//                         cached payload was used because the live
//                         fetch hit a network error and a recent
//                         cached entry was available)
//   * 'unauthenticated' — /me returned 401 (missing or expired cookie)
//   * 'error'           — non-401 server error, OR offline at boot
//                         with no usable cached payload
//
// Offline-warm-cache survival path:
//   On every successful /me, the response is mirrored to localStorage
//   via auth-cache.saveCachedMe. On bootstrap, if the network fetch
//   rejects with an offline-shaped error (TypeError from fetch, etc.
//   — see auth-cache.isOfflineNetworkError), we read the cached
//   payload and treat the user as 'authenticated'. A 401 always
//   clears the cache so a server-revoked session can never be
//   re-authenticated by a stale cache the next time we go offline.
//   See auth-cache.ts for the full trust-model rationale.
//
// logout() performs a top-level POST navigation to /auth/logout.
// The backend replies 302 to Microsoft Entra's logout endpoint so
// the IdP session is also torn down; only a real form submit lets
// the browser follow that redirect across origins (a fetch would
// either swallow the 302 or trip CORS). We do NOT optimistically
// clear local auth state — if the submit fails (misconfigured
// secret, backend 5xx), the backend redirects back into the SPA
// with ?logout_error=server and the next /me bootstrap resolves the
// real state. Flipping the state client-side before the network
// call would desync the UI with what the server actually did.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import * as Sentry from '@sentry/react'

import { ApiError, submitLogout } from '../hooks/api'
import { fetchMe, type MeResponse } from '../hooks/use-me'
import { flushAllCaptureClients } from '../capture/client/capture-client-registry'
import {
  clearPowerSyncLocalDb,
  getHeldCaptureLoad,
  hasHeldCaptureLoad,
  type HeldCaptureLoad,
} from '../hooks/powersync/logout-cleanup'

import { LogoutHeldDataDialog } from './logout-held-data-dialog'
import { preserveHeldThenAudit } from './preserve-held-captures-audit'
import { preserveUploadQueueThenAudit } from './preserve-upload-queue-audit'
import { stashHeldWorkOnLogoutThenAudit } from './stash-held-work-audit'

import {
  clearCachedMe,
  isOfflineNetworkError,
  loadCachedMe,
  saveCachedMe,
} from './auth-cache'

export type AuthStatus =
  | 'loading'
  | 'authenticated'
  | 'unauthenticated'
  | 'error'

export interface AuthContextValue {
  status: AuthStatus
  user: MeResponse | undefined
  error: Error | undefined
  logout: () => void
  refresh: () => void
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

// Upper bound on how long logout waits for the local SQLite clear before it
// gives up and signs out anyway. Generous on purpose: a healthy
// disconnectAndClear is sub-second, so this only trips for a genuinely wedged
// PowerSync worker (a documented failure mode on this app — Android Chrome
// SharedWorker / OPFS hangs). Bounding it keeps a wedge from trapping a field
// surveyor on a shared device forever; the timeout path is reported, not
// silent, so a possibly-uncleared device is visible to operators.
const LOGOUT_CLEAR_TIMEOUT_MS = 10_000

// Upper bound on the held-load preflight read. getHeldCaptureLoad() reads
// local SQLite, so it is normally sub-second; this only trips if the same
// wedged worker that proceedLogout() guards also hangs the read. On timeout
// we resolve `indeterminate` (NOT empty) so the flow fails closed to the loud
// warning instead of trapping the user before the clear-timeout even starts,
// or wiping a preserved count unseen. (Codex P2)
const HELD_LOAD_PROBE_TIMEOUT_MS = 3_000

// Upper bound on the pre-probe coalesce flush (#726). flushAllCaptureClients()
// drains each mounted capture client's in-memory 500 ms coalesce to durable
// storage so a +/- tap taken just before sign-out is persisted and the held
// guard below can see it. A healthy flush is sub-second; the bound only trips
// on a wedged write. On timeout OR rejection we FAIL CLOSED — see flushHeldWork.
const HELD_FLUSH_TIMEOUT_MS = 3_000

// Drain mounted capture clients' in-memory coalesce before the held-count
// probe, bounded so a wedged write can't trap the logout flow. Returns
// 'flushed' only when every client's flush resolved within the bound;
// 'failed' on a timeout OR a rejected flush. Both failure modes are treated as
// "a count may be unflushed and therefore unseen by the guard" → the caller
// fails closed to the loud held-data dialog rather than proceeding to the
// destructive clear with a tap potentially still trapped in memory (#726).
async function flushHeldWork(): Promise<'flushed' | 'failed'> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race<'flushed' | 'failed'>([
      flushAllCaptureClients().then(
        () => 'flushed' as const,
        () => 'failed' as const,
      ),
      new Promise<'failed'>((resolve) => {
        timer = setTimeout(() => {
          resolve('failed')
        }, HELD_FLUSH_TIMEOUT_MS)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

// Read the device's held-capture load, bounded so a hung read can't trap the
// logout flow. getHeldCaptureLoad() never rejects (its probes fail closed to
// `indeterminate`); the race only adds the timeout → `indeterminate` arm.
async function probeHeldLoad(): Promise<HeldCaptureLoad> {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    return await Promise.race<HeldCaptureLoad>([
      getHeldCaptureLoad(),
      new Promise<HeldCaptureLoad>((resolve) => {
        timer = setTimeout(() => {
          resolve({ status: 'indeterminate' })
        }, HELD_LOAD_PROBE_TIMEOUT_MS)
      }),
    ])
  } finally {
    if (timer !== undefined) clearTimeout(timer)
  }
}

export interface AuthProviderProps {
  children: ReactNode
  // Test seam — unit tests inject a deterministic fetcher so we do not
  // have to stub global fetch to exercise the state machine.
  fetcher?: () => Promise<MeResponse>
}

export function AuthProvider({
  children,
  fetcher = fetchMe,
}: AuthProviderProps): ReactNode {
  const [status, setStatus] = useState<AuthStatus>('loading')
  const [user, setUser] = useState<MeResponse | undefined>(undefined)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [refreshTick, setRefreshTick] = useState(0)
  // Non-null while the logout flow is blocked on a loud "you have N
  // un-uploaded counts" warning (audit-2026-06-22 C4 / #696). The dialog is
  // rendered by this provider; the confirm/cancel handlers are local, so the
  // public AuthContextValue is unchanged.
  const [heldLogoutLoad, setHeldLogoutLoad] = useState<HeldCaptureLoad | null>(
    null,
  )

  // The fetch happens inside useEffect so React drives its lifecycle.
  // setState calls only happen in the .then/.catch microtasks, never
  // synchronously in the effect body — required by React 19's
  // react-hooks/set-state-in-effect lint rule. The `cancelled` flag
  // covers StrictMode's double-invoke and fast remounts.
  //
  // Failure-path branching:
  //   - 200            → save to cache, authenticated.
  //   - 401            → clear cache, unauthenticated. A revoked
  //                      session must never be revived from cache.
  //   - offline shape  → try cached /me. If hit, treat as
  //                      authenticated (warm-cache offline path).
  //                      If miss, surface as error so the UI can
  //                      tell the user "you need to be online once
  //                      first".
  //   - other (5xx, etc.) → error, do NOT touch the cache. A
  //                      server hiccup shouldn't invalidate a still-
  //                      valid local session.
  useEffect(() => {
    let cancelled = false
    fetcher().then(
      (me) => {
        if (cancelled) return
        saveCachedMe(me)
        setUser(me)
        setStatus('authenticated')
        setError(undefined)
      },
      (err: unknown) => {
        if (cancelled) return
        if (err instanceof ApiError && err.status === 401) {
          clearCachedMe()
          setUser(undefined)
          setStatus('unauthenticated')
          setError(undefined)
          return
        }
        if (isOfflineNetworkError(err)) {
          const cached = loadCachedMe()
          if (cached !== null) {
            setUser(cached)
            setStatus('authenticated')
            setError(undefined)
            return
          }
          // Offline AND no cache — fall through to the error path.
        }
        setUser(undefined)
        setStatus('error')
        setError(err instanceof Error ? err : new Error(String(err)))
      },
    )
    return () => {
      cancelled = true
    }
  }, [fetcher, refreshTick])

  const refresh = useCallback(() => {
    setRefreshTick((t) => t + 1)
  }, [])

  // Guards the terminal (navigation-causing) logout path against re-entry: a
  // double-tap on the sign-out icon or the discard button must not issue the
  // clear + form-submit twice. Never reset — once we start navigating away,
  // this provider instance is on its way out. Set only when we actually
  // proceed, so the block → "stay" → retry flow is unaffected.
  const proceedingRef = useRef(false)

  // Run the destructive part of logout: clear the local PowerSync SQLite
  // mirror, then navigate to the server logout. Shared between the normal
  // path (no held counts) and the confirmed-discard path.
  //
  // M18 (audit-2026-06-22): the previous implementation raced the clear
  // against a 5s timeout and then called submitLogout() unconditionally — so
  // a merely-slow clear let the page-navigating form submit fire while
  // disconnectAndClear was still mid-flight, abandoning a partially-cleared
  // SQLite DB to the next shared-device user, SILENTLY. We now (a) bound the
  // clear generously enough that a healthy-but-slow clear completes before we
  // navigate, and (b) on the rare timeout, REPORT it before proceeding rather
  // than failing closed forever — a genuinely wedged worker must not trap a
  // field surveyor on a shared device. A reject also proceeds (a transient
  // clear error must not trap logout). Field staff share devices (admin-layout
  // sign-out and capture-user-menu both flow here). logout-cleanup.ts
  // documents the OPFS-outlives-the-tab constraint. Codex P2 on PR #509.
  const proceedLogout = useCallback(
    async (load: HeldCaptureLoad | null): Promise<void> => {
      if (proceedingRef.current) return
      proceedingRef.current = true

      // H8/H9 (#696/#663): when the device is holding un-uploaded counts,
      // PRESERVE the device-local quarantine to the durable server recovery
      // store BEFORE the clear wipes it, and AUDIT the outcome. Recovery is now
      // wired into the real sign-out path instead of a hidden pilot-only route,
      // so a surveyor who confirms the discard no longer loses held counts
      // silently. Skipped when nothing is held (known-empty load) so a clean
      // sign-out is unchanged — no extra network call, no extra audit.
      if (load !== null && hasHeldCaptureLoad(load)) {
        const userId = user?.userID
        if (navigator.onLine === false && userId !== undefined) {
          // F3 (#809) TIER 3 — OFFLINE sign-out. The online rescues below POST to
          // the recovery inbox, which is unreachable offline, so they would audit a
          // discard and the clear would DESTROY the un-uploaded counts. Instead
          // stash ALL held work (quarantine + ps_crud queue) into durable on-device
          // IndexedDB that SURVIVES the clear, keyed to THIS user, for re-inject on
          // this same user's next online login (auto-rescue-held-captures.tsx). The
          // doomed online POSTs are skipped. Best-effort + bounded — never traps the
          // surveyor on a shared device.
          await stashHeldWorkOnLogoutThenAudit(
            userId,
            load,
            new Date().toISOString(),
          )
        } else {
          await preserveHeldThenAudit(load)
          // F3 (#809): the rescue above ships only the quarantine. Also rescue the
          // ps_crud upload queue (the "H10" dimension the clear would otherwise
          // discard) to the same durable inbox before the clear. Run it whenever we
          // can't prove the queue is empty: a known non-empty queue (count > 0) OR
          // an INDETERMINATE device — the rescue reads via getCrudBatch, which is
          // independent of the getUploadQueueStats probe that failed, so an
          // unverifiable device may still have a rescuable queue (audit bh-3 / ps-F;
          // the 7,119-op loss must not re-enter through the indeterminate door).
          // Skipped only on a KNOWN-EMPTY queue. Best-effort + bounded — never traps
          // the surveyor.
          const queued = load.status === 'known' ? load.uploadQueueCount : null
          if (queued === null || queued > 0) {
            await preserveUploadQueueThenAudit(queued)
          }
        }
      }

      let timer: ReturnType<typeof setTimeout> | undefined
      try {
        const outcome = await Promise.race<'cleared' | 'timeout'>([
          clearPowerSyncLocalDb().then(() => 'cleared' as const),
          new Promise<'timeout'>((resolve) => {
            timer = setTimeout(() => {
              resolve('timeout')
            }, LOGOUT_CLEAR_TIMEOUT_MS)
          }),
        ])
        if (outcome === 'timeout') {
          Sentry.captureMessage(
            'logout: PowerSync local clear did not finish within the timeout — ' +
              'signing out anyway; the shared device may be left partially cleared',
            'warning',
          )
        }
      } catch {
        // Non-fatal: next sign-in's sync (and the OS eventually clearing OPFS
        // origin storage) is the fallback hygiene path.
      } finally {
        if (timer !== undefined) clearTimeout(timer)
      }
      submitLogout()
    },
    [user],
  )

  const logout = useCallback((): void => {
    // Ignore a re-tap once a sign-out is already committing. proceedLogout sets
    // proceedingRef (and never resets — the provider is navigating away), so a
    // second tap during the async rescue+clear window would otherwise re-flush,
    // re-probe, and re-open the held dialog OVER the in-flight logout (the
    // rescue is read-only, so the probe still sees the held rows). No integrity
    // risk — proceedingRef already blocks a double rescue/clear — but it stops
    // the confusing dialog-flicker-over-navigation on a shared device.
    if (proceedingRef.current) return
    // Drop the cached /me synchronously before anything async. If we didn't,
    // a returning offline visit after logout would resurrect the user via
    // the cache while the cookie was gone — they'd see the app as
    // authenticated until the first request hit a 401. Kept synchronous: the
    // logout tests assert the cache is null immediately after the click.
    clearCachedMe()
    // C4 (#696): the local clear wipes the ps_crud upload queue AND the
    // orphaned_capture_items quarantine — un-uploaded contractual counts
    // that live ONLY on this device. Before destroying them, check whether
    // any exist (or can't be verified); if so, STOP and raise a loud warning
    // so the surveyor decides, rather than losing counts silently on
    // sign-out. When nothing is held, proceed exactly as before.
    void (async () => {
      // #726: a +/- tap taken just before sign-out can still be sitting in a
      // mounted capture client's in-memory 500 ms coalesce — not yet in
      // ps_crud, so the held-count probe below cannot see it. Drain every
      // registered client FIRST (bounded). On a flush timeout OR rejection we
      // CANNOT prove the device is empty, so fail closed to the loud dialog
      // (treat as held) rather than clear a count still trapped in memory. The
      // indeterminate load drives the existing "couldn't check this device"
      // copy and leaves the held-total contract untouched.
      if ((await flushHeldWork()) === 'failed') {
        if (user !== undefined) saveCachedMe(user)
        setHeldLogoutLoad({ status: 'indeterminate' })
        return
      }
      const load = await probeHeldLoad()
      if (hasHeldCaptureLoad(load)) {
        // We are NOT signing out — restore the /me cache clearCachedMe()
        // dropped above, so choosing "Stay signed in" doesn't cost the user
        // their offline-warm-cache survival entry. (Codex P2)
        if (user !== undefined) saveCachedMe(user)
        setHeldLogoutLoad(load)
        return
      }
      // Known-empty load: nothing to preserve, proceed exactly as before.
      await proceedLogout(load)
    })()
  }, [proceedLogout, user])

  // Dialog dismissed without signing out — stay signed in, nothing cleared.
  const cancelLogout = useCallback((): void => {
    setHeldLogoutLoad(null)
  }, [])

  // Surveyor confirmed sign-out. proceedLogout first preserves the held
  // quarantine to the recovery store and audits the discard (it is NOT a silent
  // wipe). Capture the load before clearing the dialog state so proceedLogout
  // can drive the preserve + audit off it.
  const confirmLogoutDiscardingHeld = useCallback((): void => {
    const load = heldLogoutLoad
    setHeldLogoutLoad(null)
    void proceedLogout(load)
  }, [heldLogoutLoad, proceedLogout])

  const value = useMemo<AuthContextValue>(
    () => ({ status, user, error, logout, refresh }),
    [status, user, error, logout, refresh],
  )

  return (
    <AuthContext.Provider value={value}>
      {children}
      <LogoutHeldDataDialog
        load={heldLogoutLoad}
        onCancel={cancelLogout}
        onConfirm={confirmLogoutDiscardingHeld}
      />
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === undefined) {
    throw new Error('useAuth must be used inside <AuthProvider>')
  }
  return ctx
}
